import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMovieDetail, useMovieCast, useMoviesByCategory } from '../hooks/useMovie';
import { toTitle, toTitles } from '../lib/catalogue';
import { TitleDetailView } from '../components/title-detail-view';
import { SubscribeBand } from '../components/subscribe-band';
import { Button } from '../components/ui/button';
import { Icon } from '@iconify/react';
import { usePageTitle } from '../hooks/usePageTitle';

export default function MoviePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useMovieDetail(id);
  const { data: cast = [] } = useMovieCast(id);
  const { data: relatedData = [] } = useMoviesByCategory(data?.CategoryId);

  const formattedTitle = useMemo(() => {
    if (!data) return null;
    const base = toTitle(data);
    return {
      ...base,
      seasons: data.Seasons || [],
      episodes: data.Episodes || [],
      rating: data.AgeRating || '16+',
      duration: data.Duration || (data.DurationSeconds ? `${Math.round(data.DurationSeconds / 60)}m` : null),
    };
  }, [data]);

  const formattedCast = useMemo(() => {
    return (cast || []).map((c) => ({
      id: c.Id || c.ActorId,
      name: c.Name || c.ActorName,
      character: c.Character || c.CharacterName || c.Role,
      photo: c.PhotoUrl || c.ImageUrl || c.AvatarUrl,
    }));
  }, [cast]);

  const relatedTitles = useMemo(() => {
    const list = Array.isArray(relatedData) ? relatedData : relatedData?.data || [];
    return toTitles(list.filter((m) => String(m.Id || m.id) !== String(id)));
  }, [relatedData, id]);

  usePageTitle(
    formattedTitle ? `${formattedTitle.title} · Kast` : 'Kast',
    formattedTitle?.description
  );

  if (isLoading) {
    return (
      <div className="flex flex-col min-h-screen">
        <div className="w-full aspect-[21/9] min-h-[440px] max-h-[720px] bg-card/20 animate-pulse" />
        <div className="mx-auto max-w-[1600px] w-full gutter py-10 space-y-6">
          <div className="h-8 w-64 rounded-lg bg-white/5 animate-pulse" />
          <div className="h-4 w-96 rounded bg-white/5 animate-pulse" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="aspect-video rounded-xl bg-white/5 animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !formattedTitle) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center pt-20">
        <div className="text-center">
          <Icon
            icon="ph:film-slate"
            className="mx-auto size-16 text-muted-foreground/50"
          />
          <h1 className="display mt-6 text-2xl font-bold">Content not found</h1>
          <p className="mt-3 text-muted-foreground">
            This title could not be found or has been removed from the catalogue.
          </p>
          <Button onClick={() => navigate('/')} className="mt-6 rounded-full" variant="outline">
            <Icon icon="ph:arrow-left-bold" className="mr-2 size-4" />
            Back to home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <TitleDetailView
        title={formattedTitle}
        cast={formattedCast}
        relatedTitles={relatedTitles}
      />
      <SubscribeBand />
    </div>
  );
}
